/**
 * Convierte un AudioBuffer a formato WAV
 * @param {AudioBuffer} audioBuffer - Buffer de audio a convertir
 * @returns {Blob} - Blob con el audio en formato WAV
 */
export function audioBufferToWav(audioBuffer) {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;

    const data = [];
    for (let i = 0; i < numberOfChannels; i++) {
        data.push(audioBuffer.getChannelData(i));
    }

    const interleaved = interleave(data);
    const dataLength = interleaved.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);

    // WAV Header
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // formato chunk size
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true); // byte rate
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);

    // Escribir datos de audio
    let offset = 44;
    for (let i = 0; i < interleaved.length; i++) {
        const sample = Math.max(-1, Math.min(1, interleaved[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
    }

    return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Intercala múltiples canales de audio
 * @param {Array<Float32Array>} channels - Canales de audio
 * @returns {Float32Array} - Audio intercalado
 */
function interleave(channels) {
    const length = channels[0].length * channels.length;
    const result = new Float32Array(length);

    let inputIndex = 0;
    for (let i = 0; i < channels[0].length; i++) {
        for (let channel = 0; channel < channels.length; channel++) {
            result[inputIndex++] = channels[channel][i];
        }
    }

    return result;
}

/**
 * Escribe una cadena en el DataView
 * @param {DataView} view - Vista de datos
 * @param {number} offset - Offset donde escribir
 * @param {string} string - Cadena a escribir
 */
function writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
    }
}

/**
 * Convierte un Blob de audio a AudioBuffer
 * @param {Blob} blob - Blob de audio
 * @param {AudioContext} audioContext - Contexto de audio
 * @returns {Promise<AudioBuffer>} - Buffer de audio
 */
export async function blobToAudioBuffer(blob, audioContext) {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    return audioBuffer;
}

/**
 * Convierte un Blob a Base64
 * @param {Blob} blob - Blob a convertir
 * @returns {Promise<string>} - String en Base64
 */
export function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
